import { nonenumerable } from 'shared/decorators';
import { translate } from 'shared/services/I18nService';
import { createLogger } from 'shared/services/logger';
import { DRAW_MODE_NAMES } from 'shared/stores/annotations';
import { store } from 'shared/stores/core/decorators';
import us from 'uScope/js/defines';
import { Rectangle } from 'uScope/js/lib/objects/Rectangle';

const logger = createLogger('IA_DRAW_MODE store');

// Store constants
const STORE_NAME = 'IA_DRAW_MODE';
const DEFAULT_STATE = {
  iaDrawModeEnabled: false,
  iaAutoAnalyzeEnabled: true
};

// Drawing-related constants
const ENABLED_MODES = ['rect', 'pen']; // Image analysis can only be run on certain annotation types; remove the ones that aren't allowed
const DISABLED_MODES = DRAW_MODE_NAMES.filter(m => !ENABLED_MODES.includes(m));
const DRAW_MODE_LOCK = {lockOwner: 'ia.title', modes: DISABLED_MODES};

@store(STORE_NAME)
export class IaDrawingStore {
  state () {
    const state = this.stateCache.restore();
    // The state cache caches iaDrawModeEnabled, which really doesn't make sense in this case, so set it back to default.
    state.iaDrawModeEnabled = false;
    return state;
  }

  @nonenumerable
  stateCache = null;

  constructor ({factory}) {
    this.stateCache = factory.create('CACHE_PROVIDER', STORE_NAME, DEFAULT_STATE);
  }

  remap = {
    // When the normal drawing tools are hidden, always exit IA Draw Mode
    HIDE_DRAWING_TOOLS: 'IA_LEAVE_DRAW_MODE',
    IA_DRAW_MODE_STARTED: 'IA_SELECT_LAYER',
    // When IA workflow is closed release lock on IA
    IA_HIDE_TOOLS: 'IA_LEAVE_DRAW_MODE',
    // Whenever the selected macro or image changes, re-evaluate the locks (TODO - does this make sense?)
    IA_STATE_CHANGE: 'IA_EVALUATE_LAYER_SELECTION'
  };

  /* Evaluate and relay non-IA drawing events */

  // When someone starts drawing, maybe relay
  async DRAW_MODE_STARTED ({dispatch}) {
    if (this.state.iaDrawModeEnabled) {
      await dispatch('IA_DRAW_MODE_STARTED');
    }
  }

  /* IA Drawing events */

  IA_LEAVE_DRAW_MODE ({dispatch}) {
    this.state.iaDrawModeEnabled = false;
    removeIaLock(dispatch);
    dispatch('IA_DESELECT_LAYER');
  }

  async IA_TOGGLE_DRAW_MODE ({dispatch}) {
    this.state.iaDrawModeEnabled = !this.state.iaDrawModeEnabled;
    if (this.state.iaDrawModeEnabled) {
      await dispatch('LOCK_DRAW_MODES', DRAW_MODE_LOCK);
    } else {
      await removeIaLock(dispatch);
    }
    await dispatch('SHOW_DRAWING_TOOLS'); // This will potentially create layers, which is asynchronous
    await dispatch('IA_EVALUATE_LAYER_SELECTION');
  }

  // Enable or disable drawing tools that can be used by IA
  async IA_EVALUATE_LAYER_SELECTION ({dispatch}) {
    await dispatch(this.state.iaDrawModeEnabled ? 'IA_SELECT_LAYER' : 'IA_DESELECT_LAYER');
  }

  /* Events that result in analysis */

  IA_REGION_SAVED ({dispatcher}) {
    if (this.state.iaAutoAnalyzeEnabled && this.state.iaDrawModeEnabled) {
      dispatcher.dispatch('IA_ADD_JOB');
    }
  }

  // Handle the case where the user clicks the 'Current View' button (to analyze the current viewport)
  async IA_ANALYZE_CURRENT_VIEW ({dispatch, get}) {
    // Get the needed dependencies - the viewport and the annotations service
    const viewport = get('VIEWPORT').currentViewport;
    const annotationsService = get('VIEWPORT').plugins('us:annotations:service');

    if (!viewport || !annotationsService) {
      // Highly unlikely this will ever happen but we're checking so let's let the user know.
      dispatch('USCOPE_NOTIFY', {
        type: 'error',
        message: translate('messages.try-again')
      });

      logger.error('Cannot analyze current view; missing viewport and/or annotations service!', viewport, annotationsService);

      return;
    }
    // Make sure the correct layer for the current macro is setup and selected
    await dispatch('IA_SELECT_LAYER');

    // Get the layer id for use when creating the analysis region below
    const {iaInputLayer} = get('IA_ANNOTATIONS');
    const layerId = iaInputLayer.id;
    // Get the viewport bounding box in image coordinates, and the view port state
    const bBox = viewport.getVPViewRect2();
    const vpState = viewport.getVPView();

    const zoom = vpState.zoom;
    const angle = 0;
    const rectangle = new Rectangle(bBox.left, bBox.top, bBox.w, bBox.h);
    const points = rectangle.toPointArray();

    // Create and add the region; this will fire the 'us:annotations:region-added' event, relayed by the DrawingStore,
    // resulting in the region being added to the JobStore
    await annotationsService.regCreate({layerId, type: us.eAnTypes.rect, angle, zoom, points}, true);

    // Await the region being saved to the database (line above), then fire off the event that starts and tracks analysis
    dispatch('IA_ADD_JOB');
  }

  UPDATE_IA_AUTO_ANALYZE (store, {value}) {
    this.state.iaAutoAnalyzeEnabled = value;
    this.stateCache.save(this.state);
  }
}

function removeIaLock (dispatch) {
  return dispatch('UNLOCK_DRAW_MODES', DRAW_MODE_LOCK);
}



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/annotations/IaDrawingStore.js